import React, { useEffect, useRef } from 'react';
import { Network } from 'vis-network';
import { DataSet } from 'vis-data';

// Utility function to clean politician names for image URLs
const cleanNameForImage = (name) => {
  if (!name || typeof name !== 'string') return null;
  
  // Special case for Presidente da República
  if (name.includes('Presidente da República')) {
    return `/fotospoliticos/Lula.jpeg`;
  }
  
  // Skip special cases that aren't real people
  if (name === 'Ainda não definido' || 
      name === '-' || 
      name.includes('Presidência') ||
      name.includes('Leis') ||
      name.includes('Lei') ||
      name.includes('Presidente Da República')) return null;
  
  // Remove party info and titles
  const cleanedName = name.replace(/\s*\([^)]*\)\s*/g, '').trim();
  
  // Map politician names to image filenames
  const specialMappings = {
    'Sidney Leite': 'SidneyLeite.png',
    'Sidney Leite (PSD-AM)': 'SidneyLeite.png',
    'SIDNEY LEITE': 'SidneyLeite.png',
    'Sidney': 'SidneyLeite.png',
    'Julio Lopes': 'JulioLopes.png',
    'Laura Carneiro': 'LauraCarneiro.png',
    'Delegado Paulo Bilynskyj': 'DelegadoPauloBilynskyj.png',
    'Paulo Bilynskyj': 'DelegadoPauloBilynskyj.png',
    'Aureo Ribeiro': 'AureoRibeiro.jpg',
    'Áureo Ribeiro': 'AureoRibeiro.jpg',
    'Gilberto Abramo': 'GilbertoAbramo.png',
    'Marcelo Crivella': 'MarceloCrivella.png',
    'Hildo Rocha': 'HildoRocha.png',
    'Marcos do Val': 'MarcosDoVal.jpg',
    'Eros Biondini': 'ErosBiondini.jpg',
    'Caio Vianna': 'CaioVianna.jpg',
    'Luizão Goulart': 'LuizaoGoulart.png',
    'Reginaldo Lopes': 'ReginaldoLopes.png',
    'Geninho Zuliani': 'GeninhoZuliani.png',
    'Julia Zanatta': 'JuliaZanatta.png',
    'Adriana Ventura': 'AdrianaVentura.png',
    'Kiko Celeguim': 'KikoCeleguim.png',
    'Guilherme Boulos': 'GuilhermeBoulos.png',
    'Otto Alencar Filho': 'OttoAlencarFilho.png',
    'Ricardo Ayres': 'RicardoAyres.png',
    'Silas Câmara': 'SilasCamara.png',
    'Eduardo Braga': 'EduardoBraga.png',
    'Cleber Verde': 'CleberVerde.png',
    'Erika Kokay': 'ErikaKokay.png',
    'Rodrigo Pacheco': 'RodrigoPacheco.png',
    'Rogério Carvalho': 'RogerioCarvalho.png',
    'Carlos Portinho': 'CarlosPortinho.png',
    'Gilson Daniel': 'GilsonDaniel.png',
    'Marcos Pontes': 'MarcosPontes.png',
    'Danilo Forte': 'DaniloForte.png',
    'Mario Negromonte Jr.': 'MarioNegromonteJr.png',
    'Mario Negromonte Jr': 'MarioNegromonteJr.png',
    'Mario Negromonte': 'MarioNegromonteJr.png',
    'Mário Negromonte Jr.': 'MarioNegromonteJr.png',
    'Mário Negromonte Jr': 'MarioNegromonteJr.png',
    'Carolini de Toni': 'CarolineDeToni.png',
    'Caroline de Toni': 'CarolineDeToni.png',
    'Nely Aquino': 'NelyAquino.png',
    'Fabio Schiochet': 'FabioSchiochet.png',
    'Davi Alcolumbre': 'DaviAlcolumbre.png',
    'Lucas Ramos': 'LucasRamos.png',
    'Hugo Motta': 'HugoMotta.png',
  };
  
  if (specialMappings[cleanedName]) {
    return `${process.env.PUBLIC_URL}/fotospoliticos/${specialMappings[cleanedName]}`;
  }
  
  return null; // Only use explicit mappings, don't try to guess
};

// Generate a unique ID for nodes that might have identical labels
const generateNodeId = (label, type, index) => {
  // For undefined/unknown nodes, create unique IDs by adding the row index
  if (label === 'Ainda não definido' || label?.includes('Responsável ainda não definido')) {
    return `${type}_undefined_${index}`;
  }
  return `${type}_${label.replace(/[^a-zA-Z0-9]/g, '_')}`;
};

// Helper function to get the step number for the status (copied from DataTable.jsx)
const getStatusStep = (status) => {
  if (!status) return 0;
  const lowerStatus = status.toLowerCase().trim();
  if (lowerStatus.startsWith('aberto para consulta pública')) return 1;
  if (lowerStatus.startsWith('consulta pública encerrada')) return 2;
  if (lowerStatus.startsWith('aguardando designação de relator')) return 3;
  if (lowerStatus.startsWith('pronta para pauta na comissão') || lowerStatus.startsWith('pronto para pauta no plenário')) return 4;
  if (lowerStatus.startsWith('aguardando parecer do(a) relator(a)')) return 5;
  if (lowerStatus.startsWith('aprovada') || [
      'sancionada pelo presidente lula', 
      'sancionada pelo presidente jair bolsonaro'
     ].includes(lowerStatus)) return 6;
  if (lowerStatus.startsWith('aguardando')) return 3; // Generic waiting
  return 0; // Default/Unknown
};

// Define colors for each status step (copied from DataTable.jsx)
const stepColors = [
  '#888888', // Step 0 (Unknown/Default)
  '#f44336', // Step 1 (Red)
  '#ff9800', // Step 2 (Orange)
  '#ffc107', // Step 3 (Amber)
  '#cddc39', // Step 4 (Lime)
  '#8bc34a', // Step 5 (Light Green)
  '#4caf50', // Step 6 (Green)
];

// Format node label, replacing special text with emoji while keeping original text for hover
const formatLabel = (label) => {
  // Handle null or undefined labels
  if (!label) return { label: '?', title: '?' };
  
  // Replace "Lei já aprovada" with checkmark emoji
  if (label.includes("Lei já aprovada")) {
    return {
      label: "✅",
      title: label // Keep original text for hover
    };
  }
  
  // Handle other bill indicators
  if (label.includes("Lei") || label.includes("Leis")) {
    return {
      label: "📜 " + label,
      title: label
    };
  }
  
  // Handle undefined cases
  if (label === 'Ainda não definido' || label.includes('Responsável ainda não definido')) {
    return { label: '?', title: label };
  }
  
  // Default: return the text without party info
  const cleanLabel = label.split(' (')[0];
  return { label: cleanLabel, title: label };
};

const GraphVisualization = ({ data, onClose }) => {
  const containerRef = useRef(null);
  const networkRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;
    
    console.log("Graph visualization container exists");
    
    try {
      // Transform data into graph structure
      const nodes = new DataSet();
      const edges = new DataSet();
      const clusters = {};
      
      // Skip processing if data is empty or invalid
      if (!data || data.length === 0) {
        console.error("No data available for visualization");
        return;
      }
      
      // Process data to create nodes and edges
      data.forEach((row, index) => {
        const patrocinador = row.Patrocinador;
        const acao = row.Acao || `Proposta ${index + 1}`;
        const relator = row.RelatorAtual;
        const responsavel = row.ResponsavelTramitacao;
        const orgao = row.OrgaoAtual;
        const tema = row.Tema;
        const status = row.Status; // Get the status
        
        // Get the color based on status
        const statusStep = getStatusStep(status);
        const edgeColor = stepColors[statusStep] || stepColors[0];
        
        // Skip empty entries
        if (!patrocinador || patrocinador === '-') return;
        
        // Generate unique IDs for nodes
        const patrocinadorId = generateNodeId(patrocinador, 'patrocinador', index);
        const relatorId = relator && relator !== '-' ? generateNodeId(relator, 'relator', index) : null;
        const responsavelId = responsavel && responsavel !== '-' ? 
          generateNodeId(responsavel, 'responsavel', index) : null;
        
        // Add edges with simple styling
        const edgeId = `edge_${index}`;
        
        // Check if this is a multi-sponsor node and split it
        if (patrocinador.includes('Kiko Celeguim') && patrocinador.includes('Guilherme Boulos')) {
          // Create individual nodes for Kiko and Boulos
          const sponsors = ['Kiko Celeguim', 'Guilherme Boulos'];
          
          sponsors.forEach(sponsor => {
            const sponsorId = generateNodeId(sponsor, 'patrocinador', index + (sponsor === 'Kiko Celeguim' ? 0.1 : 0.2));
            const sponsorFormatted = formatLabel(sponsor);
            
            if (!nodes.get(sponsorId)) {
              // Use direct image paths
              let imageUrl = `/fotospoliticos/${sponsor === 'Kiko Celeguim' ? 'KikoCeleguim.png' : 'GuilhermeBoulos.png'}`;
              
              nodes.add({ 
                id: sponsorId, 
                label: sponsorFormatted.label,
                title: sponsorFormatted.title,
                group: 'sponsor',
                shape: 'circularImage',
                image: imageUrl,
                color: '#2196f3', // Blue
              });
            }
            
            // Create identical edges from both sponsors
            if (relatorId) {
              edges.add({
                id: `${edgeId}_${sponsor.replace(/\s+/g, '_')}`,
                from: sponsorId,
                to: relatorId,
                label: acao,
                title: row.Objetivo || acao,
                color: edgeColor, // Use status-based color
              });
            } else if (responsavelId) {
              edges.add({
                id: `${edgeId}_${sponsor.replace(/\s+/g, '_')}`,
                from: sponsorId,
                to: responsavelId,
                label: acao,
                title: row.Objetivo || acao,
                color: edgeColor, // Use status-based color
              });
            }
          });
          
          // Skip the normal node creation for this combined entry
          return;
        }
        
        // Handle special label replacements
        const patrocinadorFormatted = formatLabel(patrocinador);
        const relatorFormatted = relator ? formatLabel(relator) : null;
        const responsavelFormatted = responsavel ? formatLabel(responsavel) : null;
        
        // Add nodes if they don't exist
        if (!nodes.get(patrocinadorId)) {
          const imageUrl = cleanNameForImage(patrocinador);
          
          // Special case for Sidney Leite
          let finalImageUrl = imageUrl;
          if (patrocinador && (
              patrocinador.includes('Sidney Leite') || 
              patrocinador === 'Sidney' ||
              patrocinador === 'SIDNEY LEITE'
            )) {
            console.log("Adding Sidney Leite node, image path:", `${process.env.PUBLIC_URL}/fotospoliticos/SidneyLeite.png`);
            console.log("process.env.PUBLIC_URL value:", process.env.PUBLIC_URL);
            // Try direct path approach
            finalImageUrl = `/fotospoliticos/SidneyLeite.png`;
          }
          
          nodes.add({ 
            id: patrocinadorId, 
            label: patrocinadorFormatted.label,
            title: patrocinadorFormatted.title,
            group: 'sponsor',
            shape: finalImageUrl ? 'circularImage' : 'circle',
            image: finalImageUrl || undefined,
            color: '#2196f3', // Blue
          });
        }
        
        if (relatorId && !nodes.get(relatorId)) {
          const imageUrl = cleanNameForImage(relator);
          
          // Special case for Sidney Leite
          let finalImageUrl = imageUrl;
          if (relator && (
              relator.includes('Sidney Leite') || 
              relator === 'Sidney' ||
              relator === 'SIDNEY LEITE'
            )) {
            console.log("Adding Sidney Leite node as relator, image path:", `${process.env.PUBLIC_URL}/fotospoliticos/SidneyLeite.png`);
            console.log("process.env.PUBLIC_URL value for relator:", process.env.PUBLIC_URL);
            // Try direct path approach
            finalImageUrl = `/fotospoliticos/SidneyLeite.png`;
          }
          
          nodes.add({ 
            id: relatorId, 
            label: relatorFormatted.label,
            title: relatorFormatted.title,
            group: 'rapporteur',
            shape: finalImageUrl ? 'circularImage' : 'circle',
            image: finalImageUrl || undefined,
            color: '#ffeb3b', // Yellow
          });
        }
        
        if (responsavelId && !nodes.get(responsavelId)) {
          const imageUrl = cleanNameForImage(responsavel);
          
          // Special case for Sidney Leite
          let finalImageUrl = imageUrl;
          if (responsavel && (
              responsavel.includes('Sidney Leite') || 
              responsavel === 'Sidney' ||
              responsavel === 'SIDNEY LEITE'
            )) {
            console.log("Adding Sidney Leite node as responsavel, image path:", `${process.env.PUBLIC_URL}/fotospoliticos/SidneyLeite.png`);
            console.log("process.env.PUBLIC_URL value for responsavel:", process.env.PUBLIC_URL);
            // Try direct path approach
            finalImageUrl = `/fotospoliticos/SidneyLeite.png`;
          }
          
          nodes.add({ 
            id: responsavelId, 
            label: responsavelFormatted.label,
            title: responsavelFormatted.title,
            group: 'manager',
            shape: finalImageUrl ? 'circularImage' : 'circle',
            image: finalImageUrl || undefined,
            color: '#f44336', // Red
          });
        }
        
        // Track clusters for organs
        if (orgao && orgao !== '-') {
          if (!clusters[orgao]) {
            clusters[orgao] = { nodes: [] };
          }
          clusters[orgao].nodes.push(patrocinadorId);
          if (relatorId) clusters[orgao].nodes.push(relatorId);
          if (responsavelId) clusters[orgao].nodes.push(responsavelId);
        }

        // Create edges for the standard case (non-Kiko/Boulos)
        if (patrocinador.includes('Kiko Celeguim') && patrocinador.includes('Guilherme Boulos')) {
          // Skip edge creation for combined Kiko/Boulos as it's done in the special case
        } else {
          if (relatorId) {
            // Edge from sponsor to rapporteur
            edges.add({
              id: `${edgeId}_1`,
              from: patrocinadorId,
              to: relatorId,
              label: acao,
              title: row.Objetivo || acao,
              color: edgeColor, // Use status-based color
            });
            
            // Edge from rapporteur to manager if exists
            if (responsavelId) {
              edges.add({
                id: `${edgeId}_2`,
                from: relatorId,
                to: responsavelId,
                dashes: true,
                color: edgeColor, // Use status-based color but keep the dashes
              });
            }
          } else if (responsavelId) {
            // Direct edge if no rapporteur
            edges.add({
              id: edgeId,
              from: patrocinadorId,
              to: responsavelId,
              label: acao,
              title: row.Objetivo || acao,
              color: edgeColor, // Use status-based color
            });
          }
        }
      });
      
      // Skip if no nodes or edges were created
      if (nodes.length === 0) {
        console.error("No valid nodes found for visualization");
        return;
      }

      // Simplified Network options to avoid errors
      const options = {
        nodes: {
          size: 40.625,
          font: { 
            size: 12,
            color: '#FFFFFF', // Change to white for better readability
            strokeWidth: 2, // Add text stroke/outline
            strokeColor: 'rgba(0,0,0,0.7)' // Add dark outline around white text
          },
          borderWidth: 2,
          scaling: {
            label: {
              enabled: true
            }
          },
          chosen: {
            node: (values, id, selected, hovering) => {
              if (hovering) {
                values.size = 48.75;
              }
            }
          }
        },
        edges: {
          width: 1,
          arrows: 'to',
          font: { size: 10 }
        },
        physics: {
          enabled: true,
          solver: 'forceAtlas2Based'
        },
        interaction: {
          hover: true // Enable hover effects
        }
      };
      
      // Create the network with minimal configuration
      networkRef.current = new Network(
        containerRef.current,
        { nodes, edges },
        options
      );
      
      console.log("Network created successfully with", nodes.length, "nodes and", edges.length, "edges");
      
      // Add double-click handler to expand clusters
      networkRef.current.on('doubleClick', (params) => {
        if (params.nodes.length === 1) {
          const nodeId = params.nodes[0];
          if (nodeId.startsWith('cluster_')) {
            networkRef.current.openCluster(nodeId);
          }
        }
      });
      
      // Create a delay to let the network initially position the nodes
      setTimeout(() => {
        // Create and immediately expand clusters for organs
        Object.entries(clusters).forEach(([orgao, cluster]) => {
          const uniqueNodes = [...new Set(cluster.nodes)];
          if (uniqueNodes.length > 0) {
            try {
              // First create cluster
              networkRef.current.cluster({
                joinCondition: (nodeObj) => uniqueNodes.includes(nodeObj.id),
                clusterNodeProperties: {
                  id: `cluster_${orgao}`,
                  label: orgao,
                  shape: 'box',
                }
              });
              
              // Then immediately open it
              networkRef.current.openCluster(`cluster_${orgao}`);
            } catch (e) {
              console.warn(`Couldn't create/open cluster for ${orgao}:`, e);
            }
          }
        });
        
        // Fit the network to view after expanding clusters
        setTimeout(() => {
          if (networkRef.current) {
            networkRef.current.fit();
          }
        }, 500);
      }, 1000);
    } catch (error) {
      console.error("Error creating network visualization:", error);
    }
    
    // Cleanup on unmount
    return () => {
      if (networkRef.current) {
        networkRef.current.destroy();
      }
    };
  }, [data]);
  
  return (
    <div className="graph-visualization">
      <div className="graph-header">
        <div className="graph-controls">
          <p className="graph-instructions">
            Dê dois cliques em um grupo para expandir. Arraste os nós para organizar. Use a roda do mouse para zoom.
          </p>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
      </div>
      <div className="graph-legend">
        <div className="legend-item">
          <div className="legend-icon sponsor"></div>
          <span>Patrocinador</span>
        </div>
        <div className="legend-item">
          <div className="legend-icon rapporteur"></div>
          <span>Relator</span>
        </div>
        <div className="legend-item">
          <div className="legend-icon manager"></div>
          <span>Responsável pela tramit.</span>
        </div>
      </div>
      <div className="graph-container" ref={containerRef} />
    </div>
  );
};

export default GraphVisualization; 